import {
  getTauroFactorisationFromAccount,
  getTauroFactorisationLeverage,
  getTauroProfitShares,
} from '../../../common/utils/accounts'

type PaymentOption = {
  provider: string
}

export function isDepositPaymentOption({provider}: PaymentOption): boolean {
  return provider === 'enovipayCoinspaid'
}

/**
 * The minimum a deposit into a factorised Tauro account must reach (PT-7173). The backend puts
 * the campaign minimum qualifying deposit into the account minimum, in the account currency. The
 * regular rule may pick the lower client minimum instead, so an unfunded factorised account never
 * goes below the account minimum. A funded account takes no deposit and the backend adds no
 * campaign minimum for it.
 */
export function applyTauroMinimumDeposit(
  minimumDeposit: number,
  isUnfundedFactorised: boolean,
  accountMinimumDeposit: number | null | undefined,
): number {
  if (!isUnfundedFactorised || !accountMinimumDeposit) return minimumDeposit
  return Math.max(minimumDeposit, accountMinimumDeposit)
}

/**
 * The upper bound of one deposit in the account currency (PT-7175). Only an unfunded factorised
 * Tauro account has one: the campaign maximum qualifying deposit. The commit step refuses a larger
 * deposit after the payment provider has already taken the money, so the form stops it first.
 */
export function isAboveMaximumDeposit(
  amount: number | string,
  maximumDeposit: number | null | undefined,
): boolean {
  if (maximumDeposit == null || !(maximumDeposit > 0)) return false
  return +amount > maximumDeposit
}

/**
 * Drops the deposit buttons above the account maximum (PT-7175). A regular account has no maximum
 * and keeps every button. When no button fits under the maximum, the maximum is the only button.
 */
export function capPrefilledAmounts(
  amounts: number[],
  maximumDeposit: number | null | undefined,
): number[] {
  if (maximumDeposit == null || !(maximumDeposit > 0)) return amounts
  const fitting = amounts.filter((amount) => amount <= maximumDeposit)
  return fitting.length > 0 ? fitting : [maximumDeposit]
}

type DepositAccount = Parameters<typeof getTauroFactorisationFromAccount>[0]

/**
 * PT-7878: a factorised account shows in the deposit list only while a deposit may reload it, or
 * while it still waits for its first funding. A funded, liquidated, pending or closed account that
 * cannot reload takes no deposit, so the list does not offer it. Other account types stay as they
 * are. A snapshot without `status` (an older backend) keeps the earlier behaviour unless the
 * account was liquidated.
 */
export function isFactorisedAccountHiddenFromDeposit(account: DepositAccount): boolean {
  const factorisation = getTauroFactorisationFromAccount(account)
  if (factorisation == null) return false
  if (factorisation.canReload === true) return false
  if (factorisation.status != null) {
    const awaitsFirstFunding =
      factorisation.status === 'ACTIVE' && !factorisation.funded && !factorisation.funding
    return !awaitsFirstFunding
  }
  return factorisation.liquidation != null
}

/** The fixed terms of the next round of a reloadable factorised account. */
export type TauroReloadTerms = {
  nextRound: number
  factor: number
  drawdownPercent: number
  splitClient: number
  splitTauro: number
  leverage: number | null
  minDeposit: number | null
  maxDeposit: number | null
  currency: string | undefined
}

/**
 * The deposit limits of the round in the account currency. The backend converts the campaign
 * bounds for a reloadable account into `minimumDeposit` / `maximumDeposit`. The campaign values
 * are in USD, so they serve only as a fallback for a USD account while the details load.
 */
function reloadLimits(
  factorisation: {minQualifyingDeposit?: number | null; maxQualifyingDeposit?: number | null},
  currency: string | undefined,
  converted?: {minimumDeposit?: number | null; maximumDeposit?: number | null},
): {minDeposit: number | null; maxDeposit: number | null} {
  const usd = currency === 'USD'
  return {
    minDeposit: converted?.minimumDeposit ?? (usd ? factorisation.minQualifyingDeposit ?? null : null),
    maxDeposit: converted?.maximumDeposit ?? (usd ? factorisation.maxQualifyingDeposit ?? null : null),
  }
}

/** The terms for the reload note, or null when the account cannot be reloaded now. */
export function getTauroReloadTerms(
  account: DepositAccount,
  converted?: {minimumDeposit?: number | null; maximumDeposit?: number | null},
): TauroReloadTerms | null {
  const factorisation = getTauroFactorisationFromAccount(account)
  if (factorisation == null || factorisation.canReload !== true) return null
  const shares = getTauroProfitShares(factorisation)
  const currency = (account as {currency?: string}).currency ?? factorisation.currency
  return {
    nextRound: (factorisation.round ?? 1) + 1,
    factor: factorisation.multiplier ?? 0,
    drawdownPercent: factorisation.drawdownTriggerPercent ?? 0,
    splitClient: shares?.clientPercent ?? 0,
    splitTauro: shares?.tauroPercent ?? 0,
    leverage: getTauroFactorisationLeverage(factorisation),
    ...reloadLimits(factorisation, currency, converted),
    currency,
  }
}

/** Deposit, starting balance and equity floor of the new round. The factor applies once, to this deposit. */
export function getTauroReloadPreview(
  deposit: number,
  terms: Pick<TauroReloadTerms, 'factor' | 'drawdownPercent'>,
): {deposit: number; startingBalance: number; floor: number} {
  const startingBalance = deposit * terms.factor
  const floor = startingBalance - (startingBalance * terms.drawdownPercent) / 100
  return {
    deposit,
    startingBalance: Math.round(startingBalance * 100) / 100,
    floor: Math.round(floor * 100) / 100,
  }
}

export type TauroReloadAmountError = 'EMPTY' | 'METHOD_MINIMUM' | 'MINIMUM' | 'MAXIMUM'

/**
 * The first error under the amount of a reload. The payment method minimum comes before the
 * round minimum, so the client sees the limit that stops the payment first.
 */
export function getTauroReloadAmountError(
  amount: number | string | null | undefined,
  terms: Pick<TauroReloadTerms, 'minDeposit' | 'maxDeposit'>,
  methodMinimum: number | null | undefined,
): TauroReloadAmountError | null {
  const value = Number(amount)
  if (amount == null || amount === '' || !(value > 0)) return 'EMPTY'
  if (methodMinimum != null && methodMinimum > 0 && value < methodMinimum) return 'METHOD_MINIMUM'
  if (terms.minDeposit != null && value < terms.minDeposit) return 'MINIMUM'
  if (terms.maxDeposit != null && terms.maxDeposit > 0 && value > terms.maxDeposit) return 'MAXIMUM'
  return null
}

/** A money figure without the currency sign: the reload texts print the currency code after it. */
export function formatTauroReloadFigure(value: number, locale?: string): string {
  return value.toLocaleString(locale || 'en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})
}
